import type { JSX } from 'react';
import { useCallback, useContext } from 'react';
import styled from '@emotion/styled';
import { QuantAnalysisFilterContext } from '../../containers/QuantAnalysis/context';
import { NestedFilter } from '../NestedFilter';

type Props = {
  columnPaths: string[][];
  rowPaths: string[][];
  analysisName: string;
};

export function AnalysisFilterMenu({
  columnPaths,
  rowPaths,
  analysisName,
}: Props): JSX.Element {
  const [quantFilter, setQuantFilter] = useContext(QuantAnalysisFilterContext);

  const handleRowSelectionChange = useCallback(
    (paths: string[][]) => {
      setQuantFilter(current => ({ ...current, rowNames: paths }));
    },
    [setQuantFilter],
  );

  const handleColumnSelectionChange = useCallback(
    (paths: string[][]) => {
      setQuantFilter(current => ({ ...current, columnNames: paths }));
    },
    [setQuantFilter],
  );

  return (
    <FilterMenuRoot>
      <SectionTitle>Filters</SectionTitle>
      <AnalysisTabSection>
        Analyses Tab: <AnalysisTabValue>{analysisName}</AnalysisTabValue>
      </AnalysisTabSection>

      <NestedFilter
        label="Column Filters"
        paths={columnPaths}
        selectedPaths={quantFilter.columnNames}
        onSelectionChange={handleColumnSelectionChange}
      />

      <FilterSpacer />

      <NestedFilter
        label="Row Filters"
        paths={rowPaths}
        selectedPaths={quantFilter.rowNames}
        onSelectionChange={handleRowSelectionChange}
      />
    </FilterMenuRoot>
  );
}

const FilterMenuRoot = styled.div({
  padding: '16px',
  background: '#fff',
  borderRadius: '5px',
  border: '1px solid var(--gray-l)',
  minWidth: 420,
  maxWidth: 500,
});

const SectionTitle = styled.div({
  fontWeight: 600,
  marginBottom: 8,
});

const AnalysisTabSection = styled.div({
  padding: '0 0 12px 0',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--black-d)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const AnalysisTabValue = styled.span({
  fontWeight: 400,
  color: 'var(--gray-d)',
  marginLeft: 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 200,
  display: 'inline-block',
});

const FilterSpacer = styled.div({
  height: 16,
});
